import React, { FC } from 'react'
import BagPolaroidIcon from 'svg/bag-polaroid-v2.svg'
import BagPolaroidFilledIcon from 'svg/bag-polaroid-filled-v2.svg'
import styles from './Actions.module.scss'
import classNames from 'classnames'
import useAddToCart, { GA4CartEventObject } from './useAddToCart'
import { ARTWORK_STATUS } from '../helpers'
import useSearchProvider from 'components/SA/SearchProvider/hooks/useSearchProvider'
import { SAArtwork } from 'types/Artwork'
import { useSelector } from 'react-redux'
import { getIndice } from 'selectors/algolia'
import { getAlgoliaUserToken } from 'selectors/user/cookie'
import { getUserID } from 'selectors/user/session'
import { addSubNavItemsFromArtworkAttributes } from 'apps/saatchi/homepage/helpers/subNavPersonalization'

type AddToCartProps = {
  artwork: SAArtwork
  isAddedToCart?: boolean
  sku: string
  status: string
  artworkId: number
  price: number
  productEvent?: GA4CartEventObject
  customPromoCode?: string
  shouldSendAlgoliaEvents?: boolean
  hasPrints?: boolean
}

const AddToCart: FC<AddToCartProps> = ({
  artwork,
  isAddedToCart,
  sku,
  status,
  artworkId,
  productEvent,
  price,
  customPromoCode,
  shouldSendAlgoliaEvents = true,
  hasPrints,
}) => {
  const searchIndice = useSelector(getIndice)
  const algoliaUserToken = useSelector(getAlgoliaUserToken)
  const userId = useSelector(getUserID)

  const { getSearchProviderAttributes } = useSearchProvider()
  const {
    handleClickAddToCart,
    handleClickAddToCartWithPromoCode,
    shouldBlockEvents,
  } = useAddToCart({
    sku,
    searchIndice,
    algoliaUserToken,
    userId,
    artworkId,
    productEvent,
    price,
    customPromoCode,
    shouldSendAlgoliaEvents,
  })

  const handleAddToCartClick = (ev) => {
    if (customPromoCode) {
      handleClickAddToCartWithPromoCode(ev)
    } else {
      handleClickAddToCart(ev)
    }

    addSubNavItemsFromArtworkAttributes({
      artwork: {
        ...artwork,
        category: hasPrints ? 'prints' : artwork.category,
      },
      userId: userId?.toString(),
    })
  }

  if (!sku || status !== ARTWORK_STATUS.AVAILABLE) {
    return null
  }

  return (
    <button
      className={classNames(styles.iconButtonDefaultStyle, styles.iconActionContainer)}
      data-action='cart'
      data-type='action'
      title='Add to Cart'
      rel='nofollow'
      onClick={handleAddToCartClick}
      disabled={shouldBlockEvents}
      {...getSearchProviderAttributes([{ 'button-action': 'add_to_cart' }])}
    >
      <BagPolaroidFilledIcon
        className={classNames(styles.iconActionAddToCart, {
          [styles.iconActionContainerInvisible]: !isAddedToCart,
        })}
      />
      {!isAddedToCart && <BagPolaroidIcon className={styles.iconActionAddToCart} />}
    </button>
  )
}

export default AddToCart
